import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { Check } from 'lucide-react';
import { useCases } from '../data/content';

const UseCases = () => {
  const [activeTab, setActiveTab] = useState(useCases[0].id);

  return (
    <section
      id="cas-usage"
      className="py-16 md:py-24 bg-white"
      data-testid="use-cases-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl mb-12">
          <h2
            className="text-3xl md:text-4xl font-extrabold tracking-tight text-gray-900 mb-4"
            style={{ letterSpacing: '-0.04em' }}
          >
            Cas d'usage par secteur
          </h2>
          <p className="text-lg text-gray-600">
            Découvrez comment l'IA transforme concrètement le quotidien des professionnels de votre secteur.
          </p>
        </div>

        {/* Desktop: Tabs */}
        <div className="hidden md:block">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full justify-start bg-gray-100 p-1 rounded-xl mb-8">
              {useCases.map((useCase) => (
                <TabsTrigger
                  key={useCase.id}
                  value={useCase.id}
                  className="flex-1 rounded-lg py-3 px-6 text-sm font-medium transition-all data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-blue-700"
                  data-testid={`use-case-tab-${useCase.id}`}
                >
                  {useCase.title}
                </TabsTrigger>
              ))}
            </TabsList>

            {useCases.map((useCase) => (
              <TabsContent
                key={useCase.id}
                value={useCase.id}
                className="mt-0 focus-visible:outline-none"
              >
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  {/* Image */}
                  <div className="relative rounded-2xl overflow-hidden">
                    <img
                      src={useCase.image}
                      alt={useCase.title}
                      className="w-full h-80 object-cover"
                      loading="lazy"
                      width="600"
                      height="400"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-6">
                      <p className="text-white font-semibold">{useCase.result}</p>
                    </div>
                  </div>

                  {/* Content */}
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-6">
                      {useCase.title}
                    </h3>
                    <ul className="space-y-4">
                      {useCase.examples.map((example, index) => (
                        <li
                          key={index}
                          className="flex items-start gap-3"
                        >
                          <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Check className="w-4 h-4 text-blue-700" />
                          </div>
                          <span className="text-gray-700">{example}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-8 p-4 bg-blue-50 rounded-xl">
                      <p className="text-blue-800 font-semibold">
                        Résultat moyen : {useCase.result}
                      </p>
                    </div>
                  </div>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>

        {/* Mobile: Accordion */}
        <div className="md:hidden">
          <Accordion type="single" collapsible className="w-full">
            {useCases.map((useCase, index) => (
              <AccordionItem
                key={useCase.id}
                value={useCase.id}
                className="border border-gray-200 rounded-xl mb-4 overflow-hidden"
                data-testid={`use-case-accordion-${useCase.id}`}
              >
                <AccordionTrigger className="px-4 py-4 hover:no-underline">
                  <span className="text-left font-semibold text-gray-900">
                    {useCase.title}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  {/* Image */}
                  <div className="relative rounded-xl overflow-hidden mb-4">
                    <img
                      src={useCase.image}
                      alt={useCase.title}
                      className="w-full h-48 object-cover"
                      loading="lazy"
                      width="400"
                      height="200"
                    />
                  </div>

                  {/* Examples */}
                  <ul className="space-y-3 mb-4">
                    {useCase.examples.map((example, exIndex) => (
                      <li
                        key={exIndex}
                        className="flex items-start gap-2 text-sm"
                      >
                        <Check className="w-4 h-4 text-blue-700 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{example}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Result */}
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800 font-medium">
                      {useCase.result}
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

export default UseCases;
